import { useId } from 'react';
import { T, useGT } from 'gt-react';
import { resolveReportColorPalette } from '@erp/shared-types';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useReportColorPalettes } from '@/api/rpc/hooks/report-color-palettes/useReportColorPalettes';
import { PaletteSwatches } from './PaletteSwatches';

/** The Select's value for "the organization's default". */
const ORGANIZATION_DEFAULT = '__default__';
/** How many of a palette's colors the closed picker shows. */
const TRIGGER_SWATCHES = 5;

/**
 * Picks the color palette a Report draws with: the organization's default
 * (whichever that is when the Report is viewed) or a named one.
 */
export function ReportColorPaletteSelect({
  paletteId,
  onChange,
}: {
  paletteId?: string;
  /** Undefined = the organization's default. */
  onChange: (paletteId: string | undefined) => void;
}) {
  const gt = useGT();
  const triggerId = useId();
  const { data: palettes = [] } = useReportColorPalettes();
  const orgDefault = palettes.find((palette) => palette.isDefault);
  // A deleted palette reads as the default, as the Report draws it.
  const known = palettes.some((palette) => palette.id === paletteId);
  const value = paletteId && known ? paletteId : ORGANIZATION_DEFAULT;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={triggerId} className="text-xs">
        <T context="report color palette picker label">Colors</T>
      </Label>
      <Select
        value={value}
        onValueChange={(next) =>
          onChange(next === ORGANIZATION_DEFAULT ? undefined : next)
        }
      >
        <SelectTrigger id={triggerId} className="h-8 w-full text-xs">
          {/* Shorter than its option, to fit the builder's narrow sidebar. */}
          <SelectValue>
            {(selected: string) => {
              const shown =
                palettes.find((palette) => palette.id === selected) ?? null;
              return (
                <span className="flex min-w-0 items-center gap-2">
                  <PaletteSwatches
                    colors={(shown
                      ? shown.colors.categorical
                      : resolveReportColorPalette(palettes).categorical
                    ).slice(0, TRIGGER_SWATCHES)}
                  />
                  <span className="truncate">
                    {shown
                      ? shown.name
                      : orgDefault
                        ? gt('Default ({name})', {
                            name: orgDefault.name,
                            context:
                              'report color palette picker: the organization default, shown in the picker',
                          })
                        : gt('Organization default', {
                            context:
                              'report color palette picker default option',
                          })}
                  </span>
                </span>
              );
            }}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ORGANIZATION_DEFAULT}>
            <span className="flex items-center gap-2">
              <PaletteSwatches
                colors={resolveReportColorPalette(palettes).categorical}
              />
              {orgDefault
                ? gt('Organization default ({name})', {
                    name: orgDefault.name,
                    context: 'report color palette picker default option',
                  })
                : gt('Organization default', {
                    context: 'report color palette picker default option',
                  })}
            </span>
          </SelectItem>
          {palettes.map((palette) => (
            <SelectItem key={palette.id} value={palette.id}>
              <span className="flex items-center gap-2">
                <PaletteSwatches colors={palette.colors.categorical} />
                {palette.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
